import { 
  Package2, 
  Boxes, 
  Layers3,
  Archive,
  Grid3x3,
  TrendingUp,
  BarChart3
} from 'lucide-react';
import CountUp from 'react-countup';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import type { ICompanyItemInterface } from '../api/CompanyItems.interface';
import { classifyItemsByType } from '../utils/utils';

type Props = {
  items: ICompanyItemInterface[];
};

const ClassifiedItems = ({ items }: Props) => {
  const classifiedData = classifyItemsByType(items);
  
  // Get category icons and colors
  const getCategoryConfig = (_type: string, index: number) => {
    const configs = [
      { icon: Boxes, gradient: 'from-blue-500 to-cyan-600', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
      { icon: Layers3, gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
      { icon: Archive, gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
      { icon: Grid3x3, gradient: 'from-emerald-500 to-green-600', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
    ];
    return configs[index % configs.length];
  };

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {/* Main Items Card */}
      <Card className="group relative overflow-hidden border-0 bg-white shadow-sm transition-all duration-300 hover:shadow-lg dark:bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5 opacity-0 transition-opacity group-hover:opacity-100" />
        <CardHeader className="relative flex flex-row items-center gap-3 pb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 transition-transform group-hover:scale-110">
            <Package2 className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100">إجمالي الأصناف</CardTitle>
            <p className="text-xs text-slate-600 dark:text-slate-400">جميع الأصناف الفريدة</p>
          </div>
        </CardHeader>
        <CardContent className="relative pt-2">
          <div className="flex items-baseline gap-2">
            <h3 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              <CountUp
                start={0}
                end={[...new Set(items?.map((items) => items?.ItemID))].length}
                duration={1.5}
                separator=","
              />
            </h3>
            <span className="text-sm text-slate-600 dark:text-slate-400">صنف</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div className="h-full w-full bg-gradient-to-r from-primary to-primary/60 transition-all duration-1000" />
            </div>
            <Badge variant="soft" className="h-5 px-2 text-xs">
              <TrendingUp className="mr-1 h-3 w-3" />
              نشط
            </Badge>
          </div>
        </CardContent>
      </Card>
      {/* Category Cards */}
      {classifiedData.length > 0 &&
        classifiedData.map(({ type, count, totalQty }, index) => {
          const config = getCategoryConfig(type, index);
          const IconComponent = config.icon;
          
          return (
            <Card 
              key={type} 
              className={cn(
                "group relative overflow-hidden border transition-all duration-300 hover:shadow-lg",
                config.border,
                "bg-white dark:bg-slate-900"
              )}
            >
              <div className={cn(
                "absolute inset-0 bg-gradient-to-br opacity-10 transition-opacity group-hover:opacity-20",
                config.gradient
              )} />
              
              <CardHeader className="relative flex flex-row items-center gap-3 pb-2">
                <div className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-xl transition-all group-hover:scale-110",
                  config.bg,
                  "border",
                  config.border
                )}>
                  <IconComponent className={cn(
                    "h-5 w-5",
                    config.gradient.includes('blue') && "text-blue-600 dark:text-blue-400",
                    config.gradient.includes('violet') && "text-violet-600 dark:text-violet-400",
                    config.gradient.includes('amber') && "text-amber-600 dark:text-amber-400",
                    config.gradient.includes('emerald') && "text-emerald-600 dark:text-emerald-400"
                  )} />
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {type}
                  </CardTitle>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    {count} حركة
                  </p>
                </div>
              </CardHeader>
              
              <CardContent className="relative pt-2">
                <div className="space-y-3">
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                      <CountUp
                        start={0}
                        end={totalQty}
                        duration={1.5}
                        separator=","
                      />
                    </h3>
                    <span className="text-sm text-slate-600 dark:text-slate-400">وحدة</span>
                  </div>
                  
                  {/* Stats Bar */}
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-2 py-1.5 dark:bg-slate-800/50">
                    <div className="flex items-center gap-1.5">
                      <BarChart3 className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        المتوسط
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                      {count > 0 ? Math.round(totalQty / count).toLocaleString() : 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
    </div>
  );
};

export default ClassifiedItems;
